import { createContext, useContext, ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface FeatureInfo {
  featureKey: string;
  featureName: string;
  hasFeature: boolean;
  isEnabled: boolean;
  featureLimit: number;
  currentUsage: number;
  remainingUsage: number;
  canUseFeature: boolean;
  usageResetAt: string | null;
}

interface FeatureContextType {
  features: Map<string, FeatureInfo>;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
  getFeature: (featureKey: string) => FeatureInfo | undefined;
  hasFeature: (featureKey: string) => boolean;
  canUseFeature: (featureKey: string) => boolean;
  getFeatureLimit: (featureKey: string) => number;
  getFeatureUsage: (featureKey: string) => number;
  getRemainingUsage: (featureKey: string) => number;
}

const FeatureContext = createContext<FeatureContextType | undefined>(undefined);

/**
 * The feature context, or undefined outside a FeatureProvider. For optional,
 * flag-gated additions to a screen that must keep rendering where no provider
 * is mounted (isolated component tests, embedded previews): no provider = the
 * flag is simply off.
 */
export const useOptionalFeatureContext = (): FeatureContextType | undefined => useContext(FeatureContext);

export const useFeatureContext = () => {
  const context = useContext(FeatureContext);
  if (context === undefined) {
    throw new Error('useFeatureContext must be used within a FeatureProvider');
  }
  return context;
};

interface FeatureProviderProps {
  children: ReactNode;
}

export const FeatureProvider = ({ children }: FeatureProviderProps) => {
  const { user, accessScope } = useAuth();

  // Fetch all features in a single call
  const { data: featuresArray, isLoading, error, refetch } = useQuery({
    queryKey: ['all-organization-features', user?.id, accessScope],
    queryFn: async () => {
      if (!user?.id) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .rpc('get_all_organization_features');

      if (error) {
        console.error('Feature fetch error:', error);
        throw error;
      }
      
      // Map snake_case response to camelCase interface
      if (data && Array.isArray(data)) {
        return data.map((item: any) => ({
          featureKey: item.feature_key,
          featureName: item.feature_name,
          hasFeature: item.has_feature,
          isEnabled: item.is_enabled,
          featureLimit: item.feature_limit,
          currentUsage: item.current_usage,
          remainingUsage: item.remaining_usage,
          canUseFeature: item.can_use_feature,
          usageResetAt: item.usage_reset_at,
        }));
      }
      
      return [];
    },
    enabled: !!user?.id && !!accessScope,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false, // Avoid refetching on every focus
    retry: 1, // Only retry once on failure
  });

  // Convert array to Map for O(1) lookups
  const features = new Map<string, FeatureInfo>();
  if (featuresArray) {
    featuresArray.forEach(feature => {
      features.set(feature.featureKey, feature);
    });
  }

  // Helper functions for easy access
  const getFeature = (featureKey: string): FeatureInfo | undefined => {
    return features.get(featureKey);
  };

  const hasFeature = (featureKey: string): boolean => {
    return features.get(featureKey)?.hasFeature || false;
  };

  const canUseFeature = (featureKey: string): boolean => {
    return features.get(featureKey)?.canUseFeature || false;
  };

  const getFeatureLimit = (featureKey: string): number => {
    return features.get(featureKey)?.featureLimit || 0;
  };

  const getFeatureUsage = (featureKey: string): number => {
    return features.get(featureKey)?.currentUsage || 0;
  };

  const getRemainingUsage = (featureKey: string): number => {
    return features.get(featureKey)?.remainingUsage || 0;
  };

  const contextValue: FeatureContextType = {
    features,
    isLoading: isLoading || (!!user && !accessScope),
    error: error as Error | null,
    refetch: () => {
      refetch();
    },
    getFeature,
    hasFeature,
    canUseFeature,
    getFeatureLimit,
    getFeatureUsage,
    getRemainingUsage,
  };

  return (
    <FeatureContext.Provider value={contextValue}>
      {children}
    </FeatureContext.Provider>
  );
};